import {PieChart} from '@mantine/charts';
import {getThemeColor, useMantineTheme} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {ColorSwatch} from 'src/design/atoms/ColorSwatch';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconChartBar} from '@tabler/icons-react';
import fileDownload from 'js-file-download';
import type {FC} from 'react';
import {memo, useId, useMemo, useState} from 'react';
import type {AssistantChartResponse} from 'src/api/ProactApiClient';
import {AssistantChartTypeEnum} from 'src/api/ProactApiClient';
import {
  CSV_MIME_TYPE,
  buildChartCsv,
  scoutCsvFileName,
} from 'src/app/assistant/assistantCsv';
import {
  NUMERIC_CELL_STYLE,
  TABLE_COLUMN_MIN_WIDTH,
} from 'src/app/assistant/Table/chatTableFormat';
import {Button} from 'src/design/atoms/Button';
import {Table} from 'src/design/atoms/Table';
import {ChatCartesianChart} from './ChatCartesianChart';
import {
  CHART_UNAVAILABLE_TEXT,
  CHAT_CHART_COLORS,
  CHAT_PIE_SIZE,
  chartDataTable,
  chartValueAt,
  drawablePieSlices,
  formatChartValue,
  isDisplayableChart,
} from './chatChartFormat';

const PieChartWithLegend = memo(function PieChartWithLegend({
  chart,
}: {
  chart: AssistantChartResponse;
}) {
  const theme = useMantineTheme();
  const slices = useMemo(
    () =>
      chart.points.map((point, index) => ({
        name: point.x,
        value: chartValueAt(point, 0),
        color: CHAT_CHART_COLORS[index],
      })),
    [chart.points]
  );
  const drawable = useMemo(() => drawablePieSlices(slices), [slices]);

  return (
    <Stack gap="xs" align="center">
      <PieChart
        data={drawable}
        size={CHAT_PIE_SIZE}
        withTooltip
        tooltipDataSource="segment"
        valueFormatter={value => formatChartValue(value, chart.unit)}
      />
      <Stack gap={4} w="100%">
        {slices.map((slice, index) => (
          <Group key={index} gap="xs" wrap="nowrap" justify="space-between">
            <Group gap="xs" wrap="nowrap" style={{minWidth: 0}}>
              <ColorSwatch
                color={getThemeColor(slice.color, theme)}
                size={10}
                withShadow={false}
                style={{flexShrink: 0}}
              />
              <Text size="xs" truncate="end">
                {slice.name}
              </Text>
            </Group>
            <Text size="xs" style={NUMERIC_CELL_STYLE}>
              {formatChartValue(slice.value, chart.unit)}
            </Text>
          </Group>
        ))}
      </Stack>
    </Stack>
  );
});

// The chart's own data legend, not an admin record list, so it stays a plain Table.
const ChartDataTableView: FC<{chart: AssistantChartResponse; id: string}> = ({
  chart,
  id,
}) => {
  const table = chartDataTable(chart);
  if (!table) return null;
  return (
    <Box id={id}>
      <Table.ScrollContainer minWidth={table.header.length * TABLE_COLUMN_MIN_WIDTH}>
        <Table withTableBorder fz="xs" verticalSpacing={4}>
          <Table.Thead>
            <Table.Tr>
              {table.header.map((cell, index) => (
                <Table.Th key={index} ta={index > 0 ? 'right' : undefined}>
                  {cell}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {table.rows.map((row, rowIndex) => (
              <Table.Tr key={rowIndex}>
                {row.map((cell, index) => (
                  <Table.Td
                    key={index}
                    ta={index > 0 ? 'right' : undefined}
                    style={index > 0 ? NUMERIC_CELL_STYLE : undefined}>
                    {cell}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Box>
  );
};

export interface ChatChartProps {
  chart: AssistantChartResponse;
}

/**
 * Scout chart (bar, stacked bar, line, area, pie) with a "Show data" table toggle and a CSV download.
 * @registry Renders a Scout chart spec with a data-table toggle; specs it cannot draw show a muted notice.
 */
export const ChatChart: FC<ChatChartProps> = memo(function ChatChart({chart}) {
  const [showData, setShowData] = useState(false);
  const tableId = useId();
  const hasData =
    (chart.series?.length ?? 0) > 0 && (chart.points?.length ?? 0) > 0;

  return (
    <Stack gap="xs" w="100%" style={{minWidth: 0}}>
      {chart.title && (
        <Text fw={600} size="sm">
          {chart.title}
        </Text>
      )}
      {!isDisplayableChart(chart) ? (
        <Alert color="gray" icon={<IconChartBar size={16} />} p="xs">
          <Text size="xs" c="dimmed">
            {CHART_UNAVAILABLE_TEXT}
          </Text>
        </Alert>
      ) : chart.type === AssistantChartTypeEnum.Pie ? (
        <PieChartWithLegend chart={chart} />
      ) : (
        <ChatCartesianChart chart={chart} />
      )}
      {hasData && (
        <>
          <Group>
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              aria-expanded={showData}
              aria-controls={showData ? tableId : undefined}
              onClick={() => setShowData(shown => !shown)}>
              {showData ? 'Hide data' : 'Show data'}
            </Button>
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              aria-label={`Download CSV of ${chart.title || 'chart'} data`}
              onClick={() =>
                fileDownload(
                  buildChartCsv(chart),
                  scoutCsvFileName('chart', new Date()),
                  CSV_MIME_TYPE
                )
              }>
              Download CSV
            </Button>
          </Group>
          {showData && <ChartDataTableView chart={chart} id={tableId} />}
        </>
      )}
    </Stack>
  );
});
